import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Alert, Button } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
  authenticateAdmin,
  authenticateUser,
  hideAuthMessage,
  showAuthMessage,
} from "redux/actions";
import { Link, useHistory, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Formik } from "formik";
import { Form, Input } from "formik-antd";
import * as Yup from "yup";
import { ADMIN, STUDENT } from "constants/ThemeConstant";

const StaffLoginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email.")
    .required("Please enter your email."),
  newPassword: Yup.string().required("Please enter your password."),
});

const StudentLoginSchema = Yup.object().shape({
  name: Yup.string().required("Please enter your name."),
  newPassword: Yup.string().required("Please enter your password."),
});

const LoginForm = (props) => {
  const { role } = useParams();
  const history = useHistory();

  const {
    hideAuthMessage,
    loading,
    showMessage,
    messageContent,
    messageType,
    authenticateAdmin,
    authenticateUser,
    token,
    impersonatorId,
    redirect,
    allowRedirect,
    userRole,
  } = props;

  const studentInitialValues = { name: "", newPassword: "" };
  const staffInitialValues = { email: "", newPassword: "" };

  const handleLogin = (credentials) => {
    const values = { ...credentials, password: credentials.newPassword };
    delete values.newPassword;
    if (role === ADMIN) {
      authenticateAdmin(values);
    } else {
      authenticateUser({ values, role });
    }
  };

  useEffect(() => {
    if (
      ((token !== null && userRole === role) || impersonatorId != null) &&
      allowRedirect
    ) {
      history.push(redirect);
    }
    if (showMessage) {
      setTimeout(() => {
        hideAuthMessage();
      }, 3000);
    }
  });

  return (
    <>
      <motion.div
        initial={{ opacity: 0, marginBottom: 0 }}
        animate={{
          opacity: showMessage ? 1 : 0,
          marginBottom: showMessage ? 20 : 0,
        }}
      >
        <Alert
          type={messageType}
          showIcon
          message={messageContent}
          className="text-left"
        />
      </motion.div>
      <Formik
        initialValues={
          role === STUDENT ? studentInitialValues : staffInitialValues
        }
        validationSchema={
          role === STUDENT ? StudentLoginSchema : StaffLoginSchema
        }
        onSubmit={(values) => handleLogin(values)}
      >
        {({ isValid }) => (
          <Form layout="vertical" name="login-form" autoComplete="off">
            {role === STUDENT ? (
              <Form.Item name="name" label="Name" htmlFor="name" required>
                <Input
                  id="name"
                  name="name"
                  prefix={<UserOutlined className="text-primary" />}
                />
              </Form.Item>
            ) : (
              <Form.Item name="email" label="Email" htmlFor="email" required>
                <Input
                  id="email"
                  name="email"
                  prefix={<UserOutlined className="text-primary" />}
                />
              </Form.Item>
            )}
            <Form.Item
              name="newPassword"
              label={`${role === STUDENT ? "Special " : ""}Password`}
              htmlFor="newPassword"
              required
            >
              <Input.Password
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                prefix={<LockOutlined className="text-primary" />}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={!isValid}
              loading={loading}
            >
              Sign In
            </Button>

            {role === "student" ? (
              <div className="mt-3">
                <Link to="/student/signup">Sign up here</Link>
              </div>
            ) : null}

            {role === STUDENT ? null : (
              <div className="text-center mt-4">
                <Link
                  to={{
                    pathname: "/forgotpassword",
                    state: { role },
                  }}
                >
                  Forgot Password?
                </Link>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

const mapStateToProps = ({ auth }) => {
  const {
    loading,
    messageContent,
    messageType,
    showMessage,
    token,
    impersonatorId,
    userRole,
    redirect,
  } = auth;
  return {
    loading,
    messageContent,
    messageType,
    showMessage,
    token,
    impersonatorId,
    userRole,
    redirect,
  };
};

const mapDispatchToProps = {
  authenticateAdmin,
  authenticateUser,
  showAuthMessage,
  hideAuthMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
